/**
 * RC Hobby Shop — "Complete your setup" chooser (v43). Markup: snippets/rchs-setup-chooser.liquid (loaded there with
 * <script type="module">, like the other rchs modules); styles: assets/rchs-brand.css.
 *
 * Everything is server-rendered: one summary line per possible pick (with data-variant-id / data-qty / data-cents)
 * and one per vehicle variant. This module only
 *   - shows the lines that match the radios and checkboxes and sums their cents (formatting the totals with the
 *     theme's money format, the same helper Fabric's price filter uses),
 *   - swaps the button labels for the item count (server-rendered <template data-rchs-label="all-N|items-N">),
 *   - follows the buy box's variant picker (productSelect) so the vehicle line and total stay right,
 *   - adds with ONE /cart/add.js call (rchsAddItems): CartLinesUpdateEvent + the cart-items sections, then a cart
 *     refetch, so Fabric's own listeners refresh the drawer, the bubble and the live regions. The vehicle variant is
 *     read from the buy box at click time. aria-busy guards against double clicks; errors show inline.
 *   - "Add <model> + N items" gives the vehicle line the hidden property "_Setup choice" (the picked power option's
 *     label + number as printed in the chooser, e.g. "Dual 4s Completer Pack 2997"; no power options: "AA batteries"
 *     or "Vehicle only"). Staff see it on the order; snippets/rchs-cart-reminder.liquid stays quiet for that
 *     vehicle; Fabric's cart hides "_" properties. "Add the N items only" adds no property.
 * See oct11/v43/notes/cart-mechanics.md (Fabric 4.3.0 cart events).
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/** Hidden line property on the vehicle (leading "_": not shown in the cart, kept on the order for staff). */
const SETUP_CHOICE_KEY = '_Setup choice';
/** Values when the vehicle has no power options (nitro trucks). Staff-facing, English. */
const CHOICE_AA_ONLY = 'AA batteries';
const CHOICE_VEHICLE_ONLY = 'Vehicle only';

/**
 * The full cart, as product-form.js #refreshCart gets it (createCartFromAjaxResponse needs a whole cart).
 * @returns {Promise<any>}
 */
async function fetchCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    return /** @type {any} */ (cartItems).fetchCartData();
  }
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * Adds several variants with one /cart/add.js request and lets Fabric's listeners update the page.
 * @param {HTMLElement} source - Dispatches the events (never a cart-items-component, never inside quick add)
 * @param {Array<{id: number, quantity: number, properties?: Record<string, string>}>} items
 * @param {string} [tag] - detail.source; anything but 'product-form-component', and no sourceId
 * @returns {Promise<{ok: boolean, data: any}>}
 */
export async function rchsAddItems(source, items, tag = 'rchs-setup-chooser') {
  const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map(
    (element) => /** @type {HTMLElement} */ (element).dataset.sectionId
  );
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map(({ id, quantity }) => ({ merchandiseId: String(id), quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sections.join(',') }),
    });
    const data = await response.json();
    const didError = Boolean(data.status);

    if (didError) {
      source.dispatchEvent(
        new CartErrorEvent({
          error: data.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: data.description, errors: data.errors },
        })
      );
    }

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: didError ? undefined : data.sections,
        source: tag,
        didError,
      },
    });
    return { ok: !didError, data };
  } catch (error) {
    deferred.reject(error);
    source.dispatchEvent(
      new CartErrorEvent({
        error: /** @type {any} */ (error)?.message || 'Network error during add to cart',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
    throw error;
  }
}

class RchsSetupChooser extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Promise<void> | null} */
  #pendingSelect = null;

  /** The vehicle variant the summary shows. */
  #variantId = '';

  /** @type {Array<{id: number, quantity: number}>} */
  #items = [];

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#variantId = this.#readBuyBoxVariant() || this.dataset.variantId || '';

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    this.#update();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** The vehicle's selected variant in the buy box (empty when that combination does not exist). */
  #readBuyBoxVariant() {
    const productId = this.dataset.productId;
    const input =
      document.querySelector(
        `.buy-buttons-block product-form-component[data-product-id="${productId}"] input[name="id"]`
      ) ?? document.querySelector(`product-form-component[data-product-id="${productId}"] input[name="id"]`);
    return /** @type {HTMLInputElement | null} */ (input)?.value ?? '';
  }

  /** @param {Event} event */
  #onProductSelect = (event) => {
    const promise = /** @type {any} */ (event).promise;
    if (!promise?.then) return;

    const pending = promise
      .then((/** @type {any} */ result) => {
        const detail = result?.detail;
        if (String(detail?.productId ?? '') !== this.dataset.productId) return;
        // resource is the variant JSON; null when the option combination does not exist
        this.#variantId = detail?.resource ? String(detail.resource.id) : this.#readBuyBoxVariant();
        this.#hideMessages();
        this.#update();
      })
      .catch(() => {});

    this.#pendingSelect = pending;
    pending.finally(() => {
      if (this.#pendingSelect === pending) this.#pendingSelect = null;
    });
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !this.contains(input)) return;

    // Ticking or unticking a line of an option also picks that option.
    const optionId = input.dataset.rchsFor;
    if (optionId !== undefined) {
      for (const radio of this.querySelectorAll('input[data-rchs-power]')) {
        if (radio instanceof HTMLInputElement && radio.value === optionId && !radio.disabled) radio.checked = true;
      }
    }

    this.#hideMessages();
    this.#update();
  };

  /** Recomputes the visible lines, the total and the button labels from the inputs. */
  #update() {
    const power = /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-rchs-power]:checked'));
    const powerId = power?.value ?? null;

    for (const row of this.querySelectorAll('[data-rchs-option]')) {
      const id = /** @type {HTMLElement} */ (row).dataset.rchsOption;
      if (id === 'aa') continue;
      row.classList.toggle('is-selected', id === powerId);
    }

    const aaInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-rchs-aa]'));
    const aaOn = Boolean(aaInput && aaInput.checked && !aaInput.disabled);
    aaInput?.closest('.rchs-run__opt')?.classList.toggle('is-selected', aaOn);

    /** @type {Array<{id: number, quantity: number}>} */
    const items = [];
    let linesCents = 0;

    for (const line of this.querySelectorAll('li[data-rchs-line]')) {
      const { option, sub, variantId, qty, cents, available } = /** @type {HTMLElement} */ (line).dataset;
      let on = false;
      if (option === 'aa') {
        on = aaOn;
      } else if (option === powerId) {
        on = true;
        if (sub) {
          const box = /** @type {HTMLInputElement | null} */ (
            this.querySelector(`input[data-rchs-sub="${CSS.escape(sub)}"]`)
          );
          on = Boolean(box && box.checked && !box.disabled);
        }
      }
      if (available !== 'true') on = false;

      /** @type {HTMLElement} */ (line).hidden = !on;
      if (on) {
        items.push({ id: Number(variantId), quantity: Number(qty) || 1 });
        linesCents += Number(cents) || 0;
      }
    }

    let vehicleCents = 0;
    let vehicleAvailable = false;
    for (const line of this.querySelectorAll('li[data-rchs-vehicle]')) {
      const element = /** @type {HTMLElement} */ (line);
      const match = element.dataset.rchsVehicle === this.#variantId;
      element.hidden = !match;
      if (match) {
        vehicleAvailable = element.dataset.available === 'true';
        vehicleCents = vehicleAvailable ? Number(element.dataset.cents) || 0 : 0;
      }
    }

    this.#items = items;
    const count = items.length;

    const total = this.querySelector('[data-rchs-total]');
    if (total) total.textContent = this.#money(vehicleCents + linesCents);

    const addAll = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-rchs-add="all"]'));
    if (addAll) {
      this.#setLabel(addAll, `all-${count}`);
      addAll.disabled = !vehicleAvailable;
    }

    const addItems = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-rchs-add="items"]'));
    if (addItems) {
      addItems.hidden = count === 0;
      const label = addItems.querySelector('[data-rchs-items-label]');
      if (label && count > 0) this.#setLabel(label, `items-${count}`);
      const price = addItems.querySelector('[data-rchs-items-total]');
      if (price) price.textContent = this.#money(linesCents);
    }
  }

  /**
   * @param {Element} target
   * @param {string} key
   */
  #setLabel(target, key) {
    const template = this.querySelector(`template[data-rchs-label="${key}"]`);
    if (template instanceof HTMLTemplateElement) target.replaceChildren(template.content.cloneNode(true));
  }

  /** @param {number} cents */
  #money(cents) {
    const template = this.querySelector('template[data-rchs-money]');
    const format =
      (template instanceof HTMLTemplateElement ? template.content.textContent?.trim() : '') || '{{amount}}';
    return formatMoney(cents, format, this.dataset.currency || '');
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    const button = /** @type {HTMLButtonElement | null} */ (target?.closest('[data-rchs-add]') ?? null);
    if (!button || !this.contains(button) || button.disabled) return;
    if (this.hasAttribute('aria-busy')) return;

    this.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-busy', 'true');
    this.#hideMessages();

    try {
      // A variant change still in flight: wait for it, then read the buy box.
      if (this.#pendingSelect) await this.#pendingSelect;
      this.#update();

      /** @type {Array<{id: number, quantity: number, properties?: Record<string, string>}>} */
      const items = [...this.#items];
      if (button.dataset.rchsAdd === 'all') {
        const variantId = this.#readBuyBoxVariant() || this.#variantId;
        if (!variantId || !this.#vehicleAvailable(variantId)) {
          this.#showError(this.dataset.msgUnavailable || '');
          return;
        }
        items.unshift({ id: Number(variantId), quantity: 1, properties: { [SETUP_CHOICE_KEY]: this.#setupChoice() } });
      }
      if (items.length === 0) return;

      const { ok, data } = await rchsAddItems(this, items);
      if (ok) {
        this.#showDone();
      } else {
        this.#showError(data?.description || data?.message || this.dataset.msgError || '');
      }
    } catch (error) {
      console.error(error);
      this.#showError(this.dataset.msgError || '');
    } finally {
      this.removeAttribute('aria-busy');
      button.removeAttribute('aria-busy');
    }
  };

  /**
   * What the shopper chose, for staff: the checked power option's label as the chooser prints it (label + part
   * number), e.g. "Dual 4s Completer Pack 2997". Without power options (nitro): "AA batteries" when AA is ticked,
   * else "Vehicle only".
   * @returns {string}
   */
  #setupChoice() {
    const power = this.querySelector('input[data-rchs-power]:checked');
    if (power instanceof HTMLInputElement) {
      const label = this.querySelector(`label[for="${CSS.escape(power.id)}"]`);
      const text = (label?.textContent ?? '').replace(/\s+/g, ' ').trim();
      if (text) return text;
    }
    if (!this.querySelector('input[data-rchs-power]')) {
      const aa = this.querySelector('input[data-rchs-aa]');
      if (aa instanceof HTMLInputElement && aa.checked && !aa.disabled) return CHOICE_AA_ONLY;
    }
    return CHOICE_VEHICLE_ONLY;
  }

  /** @param {string} variantId */
  #vehicleAvailable(variantId) {
    const line = this.querySelector(`li[data-rchs-vehicle="${CSS.escape(variantId)}"]`);
    if (line instanceof HTMLElement) return line.dataset.available === 'true';
    // A variant this page did not render (combined listing): trust the buy box button.
    const productId = this.dataset.productId;
    const buyButton = /** @type {HTMLButtonElement | null} */ (
      document.querySelector(
        `.buy-buttons-block product-form-component[data-product-id="${productId}"] [ref="addToCartButton"]`
      )
    );
    return !buyButton?.disabled;
  }

  #showDone() {
    const done = /** @type {HTMLElement | null} */ (this.querySelector('[data-rchs-done]'));
    if (done) done.hidden = false;
    this.#announce(this.dataset.msgAdded || '');
  }

  /** @param {string} message */
  #showError(message) {
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-rchs-error]'));
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  #hideMessages() {
    const done = /** @type {HTMLElement | null} */ (this.querySelector('[data-rchs-done]'));
    if (done) done.hidden = true;
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-rchs-error]'));
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    this.#announce('');
  }

  /** @param {string} message */
  #announce(message) {
    const live = this.querySelector('[data-rchs-live]');
    if (live) live.textContent = message;
  }
}

if (!customElements.get('rchs-setup-chooser')) {
  customElements.define('rchs-setup-chooser', RchsSetupChooser);
}
